import { useMemo } from 'react';
import { compact, get } from 'lodash';
import { type CustomizableMetric, getStrategyNameForFilteredSubject, hasFeesExcluded } from 'venn-utils';
import { convertToTimeseries, isExportable } from '../../utils';
import { formatExportableNameWithOptionalFee, formatNameWithOptionalFee } from '../../../legend';
import type { Serie } from '../../../studio-blocks/components/charts/types';
import type { Analysis, RollingCorrelationResponse } from 'venn-api';
import { type StudioAnalysisRequest } from 'venn-state';
import {
  useGetBlockSubjectSuperscripts,
  usePageProxySymbol,
} from '../../components/core/block-subject-footnotes-and-superscripts';
import { useGetRequestSubjectFee } from './use-advisory-fee';

/**
 * Builds request pairs in the same deterministic combination order used by the rolling-correlation API response:
 * r1-r2, r1-r3, ... r1-rN, r2-r3, ... r(N-1)-rN.
 */
const getRollingCorrelationRequestPairs = (
  requests: StudioAnalysisRequest[],
): {
  primaryRequest: StudioAnalysisRequest;
  secondaryRequest: StudioAnalysisRequest;
}[] =>
  requests.flatMap((primaryRequest, primaryIndex) =>
    requests.slice(primaryIndex + 1).map((secondaryRequest) => ({ primaryRequest, secondaryRequest })),
  );

export const useRollingCorrelation = (
  analyses: (Analysis | undefined)[][],
  metric: CustomizableMetric | undefined,
  requests: StudioAnalysisRequest[],
  subjectHasProxy: { [subjectId: string]: boolean | undefined },
): [series: Serie[], exportableSeries: Serie[]] => {
  const proxySymbol = usePageProxySymbol();
  const getBlockSubjectSuperscripts = useGetBlockSubjectSuperscripts();
  const getRequestSubjectFee = useGetRequestSubjectFee();

  return useMemo(() => {
    if (!analyses || analyses.length < 1 || !metric) {
      return [[], []];
    }

    const series: Serie[] = [];
    const exportableSeries: Serie[] = [];

    const analysis = compact(analyses)[0]!.find((analysis) => analysis?.analysisType === 'ROLLING_CORRELATION');
    const exportable = analysis?.exportable;
    const requestPairs = getRollingCorrelationRequestPairs(requests);

    analysis?.rollingCorrelation.forEach((correlation: RollingCorrelationResponse, index) => {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const ts = metric.metricPath ? get(correlation, metric.metricPath) : correlation[0];
      if (!ts) {
        return;
      }

      // TODO: VENN-24861 Fix subject and subject name logic.
      const primarySubjectName = correlation.subjectName.name; // getSubjectLabelForChart(correlation.subjectName);
      const secondarySubjectName = correlation.secondarySubjectName.name; // getSubjectLabelForChart(correlation.secondarySubjectName);
      const indexedPair = requestPairs[index];
      const primaryRequest = indexedPair?.primaryRequest;
      const secondaryRequest = indexedPair?.secondaryRequest;

      if (!primaryRequest || !secondaryRequest) {
        return;
      }

      const primarySubject = primaryRequest.subject;
      const secondarySubject = secondaryRequest.subject;
      const primarySubjectFee = getRequestSubjectFee({
        subject: primarySubject,
        isBenchmark: primaryRequest?.isBenchmark,
      });
      const secondarySubjectFee = secondarySubject
        ? getRequestSubjectFee({ subject: secondarySubject, isBenchmark: secondaryRequest.isBenchmark })
        : undefined;
      const seriesName = `${formatNameWithOptionalFee(
        primarySubjectName,
        proxySymbol,
        getBlockSubjectSuperscripts({
          subject: primarySubject,
          isBenchmark: !!primarySubject.individualBenchmark?.benchmark,
        }),
        primarySubjectFee,
        !!subjectHasProxy[primarySubject.id],
        getStrategyNameForFilteredSubject(primarySubject),
      )} with ${formatNameWithOptionalFee(
        secondarySubjectName,
        proxySymbol,
        secondarySubject
          ? getBlockSubjectSuperscripts({
              subject: secondarySubject,
              isBenchmark: !!secondarySubject.individualBenchmark?.benchmark,
            })
          : '',
        secondarySubjectFee,
        correlation?.secondarySubjectName?.id ? !!subjectHasProxy[correlation.secondarySubjectName.id] : undefined,
        secondarySubject ? getStrategyNameForFilteredSubject(secondarySubject) : undefined,
      )}`;
      const exportableSeriesName = `${formatExportableNameWithOptionalFee(
        primarySubjectName,
        getRequestSubjectFee({ subject: primarySubject, isBenchmark: primaryRequest?.isBenchmark }),
        hasFeesExcluded(primarySubject),
        undefined,
        undefined,
      )} with ${formatExportableNameWithOptionalFee(
        secondarySubjectName,
        secondarySubject
          ? getRequestSubjectFee({ subject: secondarySubject, isBenchmark: secondaryRequest?.isBenchmark })
          : undefined,
        secondarySubject ? hasFeesExcluded(secondarySubject) : undefined,
        undefined,
        undefined,
      )}`;
      const seriesData = convertToTimeseries(ts);
      const serie = {
        name: seriesName,
        data: seriesData,
      };
      const exportableSerie = {
        name: exportableSeriesName,
        data: seriesData,
      };
      series.push(serie);

      if (
        isExportable(exportable ?? [], requests, correlation.subjectName) &&
        isExportable(exportable ?? [], requests, correlation.secondarySubjectName)
      ) {
        exportableSeries.push(exportableSerie);
      }
    });
    return [series, exportableSeries];
  }, [analyses, getBlockSubjectSuperscripts, getRequestSubjectFee, metric, proxySymbol, requests, subjectHasProxy]);
};
